import { customerName } from '@/services/db/customer/display';
import { isEmptyString } from '@/utils/string';
import type { ProfileSource } from '../types';

/** How a card names the person at its top. */
export type ProfileIdentity = {
  /** The label for the avatar; `undefined` lets Gravatar supply the picture instead. */
  avatarName: string | undefined;
  /** The line the card leads with: the person's name, else how to contact them, else their id. */
  heading: string;
  /** The contact line under the heading, shown only when the heading is a name. */
  contact: string | null;
};

/** The avatar's label: the person's name, else the local part of their email, else a placeholder. */
function avatarLabel(name: string | null, email: string, withGravatar: boolean): string | undefined {
  if (name != null) {
    return name;
  }
  if (!isEmptyString(email)) {
    return withGravatar ? undefined : email.split('@')[0];
  }
  return 'Anonymous';
}

/**
 * Who a profile card is showing. A workspace that does not identify people by
 * phone never falls back to one, so `canIdentifyByPhone` gates that.
 */
export function profileIdentity(
  profile: ProfileSource,
  { canIdentifyByPhone, withGravatar }: { canIdentifyByPhone: boolean; withGravatar: boolean },
): ProfileIdentity {
  const name = customerName(profile.attributes);
  const email = profile.attributes.email ?? '';
  const phone = canIdentifyByPhone ? (profile.identifiers?.phone ?? '') : '';
  const contact = !isEmptyString(email) ? email : !isEmptyString(phone) ? phone : null;

  return {
    avatarName: avatarLabel(name, email, withGravatar),
    heading: name ?? contact ?? profile.identifiers?.id ?? '',
    // The contact line would only repeat a heading that is already the contact.
    contact: name != null ? contact : null,
  };
}
